import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { useSeo } from "@/lib/seo";
import { ArrowRight, Search, ShieldCheck, MapPin, LayoutGrid, Aperture, Sparkles, Camera, Users, Star, Award } from "@/lib/icons";
import BannerSlider from "@/components/BannerSlider";
import CategoryStrip from "@/components/CategoryStrip";
import ComingSoonApps from "@/components/ComingSoonApps";
import MemberCard from "@/components/MemberCard";
import ProfileLink from "@/components/ProfileLink";
import ScrollReveal from "@/components/ScrollReveal";

const ICONS = { ShieldCheck, MapPin, LayoutGrid, Camera, Users, Star, Award, Sparkles };

export default function Home() {
  const [stats, setStats] = useState(null);
  const [recentMembers, setRecentMembers] = useState([]);
  const { user } = useAuth();
  const { settings } = useSettings();
  const hero = settings.hero || {};
  const features = settings.home_features || [];
  const banners = settings.home_banners || [];
  const categories = settings.categories || [];
  useSeo({
    title: "Find Photographers & Videographers Near You | PRONET",
    description: hero.description || "Find photographers, videographers and creative specialists by category and location.",
  });
  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/public/members", { params: { limit: 4, featured_only: true } })
      .then((r) => setRecentMembers(r.data.items || []))
      .catch(() => setRecentMembers([]));
    const updateViews = (event) => {
      if (event.detail?.website_views != null) {
        setStats((current) => ({ ...(current || {}), website_views: event.detail.website_views }));
      }
    };
    window.addEventListener("pronet:visitor-count", updateViews);
    return () => window.removeEventListener("pronet:visitor-count", updateViews);
  }, []);

  const StatBox = ({ label, value, testid }) => (
    <div className="relative fade-up" data-testid={testid}>
      <div className="font-display text-5xl md:text-6xl tabular-nums text-gradient font-semibold">
        {value ?? "..."}
      </div>
      <div className="overline text-white/50 mt-2">{label}</div>
    </div>
  );

  return (
    <div>
      {/* HOME BANNER SLIDER */}
      <BannerSlider banners={banners} />

      {/* HERO */}
      <section className="relative overflow-x-hidden pt-12 pb-24 md:pt-16 md:pb-40">
        <div className="blobs-layer">
          <div className="blob blob-violet w-[600px] h-[600px] -top-40 -left-20" />
          <div className="blob blob-magenta w-[520px] h-[520px] top-40 right-0" style={{ animationDelay: "-8s" }} />
          <div className="blob blob-fuchsia w-[400px] h-[400px] bottom-0 left-1/3" style={{ animationDelay: "-14s" }} />
        </div>

        <div className="container-wide relative z-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs">
              <Sparkles size={12} className="text-fuchsia-300" />
              <span className="text-white/80">{hero.badge}</span>
            </div>
            <h1 className="mt-8 font-display text-5xl sm:text-6xl md:text-7xl lg:text-[86px] font-bold leading-[0.98] tracking-tight">
              <span className="text-white">{hero.title_line1}</span><br />
              <span className="text-gradient-hero">{hero.title_accent}</span>{" "}
              <span className="text-white">{hero.title_line2}</span>
              {hero.title_tail && (<><br />
                <span className="text-white/70 text-3xl sm:text-4xl md:text-5xl lg:text-6xl">{hero.title_tail}</span>
              </>)}
            </h1>
            <p className="mt-8 text-base sm:text-lg text-white/60 max-w-xl leading-relaxed">{hero.description}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              {hero.cta_primary_label && (
                <Link to={hero.cta_primary_link || "/gomore"} data-testid="cta-search-directory"
                  className="btn-glow inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold">
                  <Search size={15} /> {hero.cta_primary_label}
                </Link>
              )}
              {user?.role === "member" ? (
                <ProfileLink to="/account" data-testid="cta-my-profile"
                  className="btn-ghost inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white">
                  My Profile
                </ProfileLink>
              ) : hero.cta_secondary_label && (
                <Link to={hero.cta_secondary_link || "/register"} data-testid="cta-register"
                  className="btn-ghost inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white">
                  {hero.cta_secondary_label}
                </Link>
              )}
              {hero.cta_tertiary_label && (
                <Link to={hero.cta_tertiary_link || "/about"} data-testid="cta-learn-more"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-white/60 hover:text-fuchsia-300 transition-colors">
                  {hero.cta_tertiary_label} <ArrowRight size={14} />
                </Link>
              )}
            </div>
          </div>

          {/* 3D-transformed showcase card */}
          <div className="md:col-span-5 relative fade-up-slow">
            <div
              className="relative aspect-[4/5] max-h-[600px] rounded-3xl overflow-hidden glass-strong noise"
              style={{ transform: "perspective(1200px) rotateY(-8deg) rotateX(4deg)" }}
            >
              <img
                src={hero.image_url}
                alt="Photography"
                className="w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/30 to-transparent" />
              <div className="absolute inset-x-5 bottom-5 glass rounded-2xl p-5">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-emerald-400" />
                  <div className="overline text-emerald-300 text-[10px]">{hero.card_overline}</div>
                </div>
                <div className="mt-2 font-display text-xl leading-tight text-white">
                  {hero.card_body}
                </div>
              </div>
            </div>

            {/* Floating stat card */}
            <div
              className="hidden md:block absolute -top-8 -left-10 glass rounded-2xl p-5 w-56 float-slow"
              style={{ transform: "perspective(800px) rotateY(6deg)" }}
            >
              <div className="overline text-fuchsia-300/80">Live count</div>
              <div className="mt-1 font-display text-4xl text-gradient tabular-nums">
                {stats?.total_members ?? "..."}
              </div>
              <div className="text-xs text-white/50">Members registered</div>
            </div>

            {/* Floating aperture badge */}
            <div
              className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 h-16 w-16 items-center justify-center rounded-full glass float-slow"
              style={{ animationDelay: "-3s" }}
            >
              <Aperture size={26} className="text-fuchsia-300" />
            </div>
          </div>
        </div>
      </section>

      {/* COMING SOON — App Store + Play Store */}
      <ComingSoonApps />

      {/* CATEGORY STRIP — placed just above the LIVE STATS card */}
      <section className="relative pb-6">
        <CategoryStrip items={categories} />
      </section>

      {/* LIVE STATS */}
      <section className="relative">
        <div className="container-wide">
          <ScrollReveal as="div" className="glass-strong rounded-3xl px-8 py-14 grid grid-cols-1 sm:grid-cols-3 gap-10">
            <StatBox testid="stat-total" label="Total members" value={stats?.total_members} />
            <StatBox testid="stat-verified" label="Pro ID members" value={stats?.verified_members} />
            <StatBox testid="stat-views" label="Website views" value={stats?.website_views} />
          </ScrollReveal>
        </div>
      </section>

      {/* NEWLY JOINED */}
      {recentMembers.length > 0 && (
        <section className="container-wide py-20" data-testid="home-recent-members">
          <ScrollReveal className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="overline text-fuchsia-300">New members</div>
              <h2 className="mt-3 font-display text-4xl md:text-5xl leading-none">
                Recently joined <span className="italic text-gradient">professionals</span>
              </h2>
            </div>
            <Link to="/gomore" className="inline-flex items-center gap-1.5 text-sm font-semibold text-fuchsia-300 hover:text-fuchsia-200">
              View all <ArrowRight size={14} />
            </Link>
          </ScrollReveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {recentMembers.map((m, i) => (
              <ScrollReveal key={m.id} delay={i * 80}>
                <MemberCard member={m} index={i} testidPrefix="home-member-card" />
              </ScrollReveal>
            ))}
          </div>
        </section>
      )}

      {/* FEATURES */}
      <section className="container-wide py-32">
        <ScrollReveal className="max-w-2xl">
          <div className="overline text-fuchsia-300">Why PRONET</div>
          <h2 className="mt-4 font-display text-5xl md:text-6xl leading-none">
            Simple search. <br />
            <span className="italic text-gradient">Better connections.</span>
          </h2>
        </ScrollReveal>
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = ICONS[f.icon] || ShieldCheck;
            return (
              <ScrollReveal key={i} className="glass rounded-3xl p-8 card-hover relative" delay={i * 100}>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-600/25 border border-violet-500/30">
                  <Icon size={18} className="text-fuchsia-200" />
                </span>
                <div className="mt-6 font-display text-2xl text-white">{f.title}</div>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{f.body}</p>
              </ScrollReveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}
